/**
 * Create-app modal for the Access catalog.
 *
 * react-hook-form + Zod via the local resolver in utils/zodResolver.
 *
 * The form is authored in the operator's vocabulary — a **Type** (the
 * reachability class) plus a separate **Protocol** — and derives the backend
 * `app_type` enum from the two (VSNP-1142):
 *
 *   Type=Private + HTTPS/HTTP/TCP/UDP → https/http/tcp/udp   (host:port target)
 *   Type=Private + IP subnet          → subnet                (CIDR target)
 *   Type=Public  + HTTPS/HTTP         → external              (FQDN patterns)
 *   Type=SaaS    (pick from catalog)  → external              (FQDN patterns from the profile, VSNP-1150)
 *
 * The SaaS catalog is server-side (VSNP-1150): profiles are curated by a
 * SuperAdmin and fetched by the parent, then passed in as `saasProfiles`. The
 * created app snapshots the profile's FQDN patterns and links back via
 * `saasCatalogId`.
 *
 * A new app is always created ENABLED; enable/disable is a table/row action, not
 * a create-time choice.
 */

import { useEffect, useRef, useState } from 'react';
import { useForm, useWatch } from 'react-hook-form';
import { ChevronDown } from 'lucide-react';
import { z } from 'zod';
import Modal from '../common/Modal';
import AccessFormField from './AccessFormField';
import { zodResolver } from '../../utils/zodResolver';
import { launchUrlFor } from '../../utils/accessLaunchUrl';
import accessService, {
  type CreateAppData,
  type AccessAppType,
  type SaasProfile,
} from '../../services/access.service';

// One FQDN pattern: a bare host ("zoom.us") or a "*." suffix ("*.zoom.us").
const FQDN_PATTERN = /^(\*\.)?([a-z0-9-]+\.)+[a-z]{2,}$/i;
// Mirrors the API's connectorGroupNameSchema.
const GROUP_NAME_RE = /^[a-z0-9][a-z0-9._-]*$/i;
// Sentinel value for the "create a new group" option in the group <select>.
const NEW_GROUP = '__new__';

/** Split a comma/whitespace/newline separated list into trimmed, non-empty parts. */
function splitPatterns(raw?: string): string[] {
  return (raw ?? '')
    .split(/[\s,]+/)
    .map((p) => p.trim())
    .filter(Boolean);
}

// Reachability class shown to the operator.
const CATEGORIES = ['private', 'public', 'saas'] as const;
type Category = (typeof CATEGORIES)[number];
// Protocols per category. 'subnet' is an IP-range target rather than a wire
// protocol, but it lives in the same selector so Private has one control.
const PRIVATE_PROTOS = ['https', 'http', 'tcp', 'udp', 'subnet'] as const;
const PUBLIC_PROTOS = ['https', 'http'] as const;
type Protocol = (typeof PRIVATE_PROTOS)[number];

const PROTO_LABEL: Record<Protocol, string> = {
  https: 'HTTPS',
  http: 'HTTP',
  tcp: 'TCP',
  udp: 'UDP',
  subnet: 'IP subnet (CIDR)',
};

/** Derive the backend app_type from (category, protocol). */
function toAppType(category: Category, protocol: Protocol): AccessAppType {
  if (category === 'public' || category === 'saas') return 'external';
  return protocol; // private: https|http|tcp|udp|subnet map 1:1
}

const appSchema = z
  .object({
    name: z
      .string()
      .min(1, 'Name is required')
      .max(128)
      .regex(/^[a-z0-9][a-z0-9._-]*$/, 'Lowercase letters, digits and . _ - only'),
    displayName: z.string().max(255).optional(),
    description: z.string().max(512).optional(),
    category: z.enum(CATEGORIES),
    protocol: z.enum(PRIVATE_PROTOS),
    targetHost: z.string().max(255).optional(),
    targetPort: z.coerce.number().int().min(1).max(65535).optional(),
    targetCidr: z.string().max(64).optional(),
    connectorGroup: z.string().max(128).optional(),
    newGroupName: z.string().max(128).optional(),
    publicHostname: z.string().max(255).optional(),
    fqdnPatternsCsv: z.string().max(4096).optional(),
    saasAppId: z.string().max(36).optional(),
    tagsCsv: z.string().max(512).optional(),
  })
  // A SaaS app is picked from the server-side catalog (VSNP-1150). Validity of
  // the id is guaranteed by the picker (it can only be set from a listed
  // profile), so the schema only requires that one was chosen.
  .refine((v) => v.category !== 'saas' || !!v.saasAppId, {
    message: 'Pick a SaaS application from the list',
    path: ['saasAppId'],
  })
  // A PRIVATE app is connector-routed and needs a group (or a new group name).
  .refine((v) => v.category !== 'private' || v.connectorGroup === NEW_GROUP || !!v.connectorGroup, {
    message: 'Select or create a connector group',
    path: ['connectorGroup'],
  })
  .refine(
    (v) => v.category !== 'private' || v.connectorGroup !== NEW_GROUP || (!!v.newGroupName && GROUP_NAME_RE.test(v.newGroupName)),
    { message: 'Enter a valid group name (letters, digits, . _ -)', path: ['newGroupName'] },
  )
  // Private + IP subnet routes by CIDR.
  .refine((v) => !(v.category === 'private' && v.protocol === 'subnet') || !!v.targetCidr, {
    message: 'An IP-subnet app needs a target CIDR',
    path: ['targetCidr'],
  })
  // Private + host protocol routes by host + port.
  .refine(
    (v) => !(v.category === 'private' && v.protocol !== 'subnet') || !!v.targetHost,
    { message: 'This app needs a target host', path: ['targetHost'] },
  )
  .refine(
    (v) => !(v.category === 'private' && v.protocol !== 'subnet') || !!v.targetPort,
    { message: 'This app needs a target port', path: ['targetPort'] },
  )
  // Public is matched by hostname, so it needs at least one valid pattern.
  .refine((v) => v.category !== 'public' || splitPatterns(v.fqdnPatternsCsv).length > 0, {
    message: 'Add at least one FQDN pattern',
    path: ['fqdnPatternsCsv'],
  })
  .refine((v) => v.category !== 'public' || splitPatterns(v.fqdnPatternsCsv).every((p) => FQDN_PATTERN.test(p)), {
    message: 'Each pattern must be a hostname or "*." suffix (e.g. youtube.com or *.youtube.com)',
    path: ['fqdnPatternsCsv'],
  });

type AppFormValues = z.infer<typeof appSchema>;

const defaults: AppFormValues = {
  name: '',
  displayName: '',
  description: '',
  category: 'private',
  protocol: 'https',
  targetHost: '',
  targetPort: 443,
  targetCidr: '',
  connectorGroup: '',
  newGroupName: '',
  publicHostname: '',
  fqdnPatternsCsv: '',
  saasAppId: '',
  tagsCsv: '',
};

interface AppModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSave: (data: CreateAppData) => Promise<void>;
  connectorGroups?: string[];
  /** SuperAdmin-curated SaaS profiles, fetched by the parent (VSNP-1150). */
  saasProfiles?: SaasProfile[];
}

const AppModal = ({ isOpen, onClose, onSave, connectorGroups = [], saasProfiles = [] }: AppModalProps) => {
  const {
    register,
    handleSubmit,
    reset,
    setValue,
    setError,
    control,
    formState: { errors, isSubmitting },
  } = useForm<AppFormValues>({ resolver: zodResolver<AppFormValues>(appSchema), defaultValues: defaults });

  // SaaS picker combobox: open state + the integrated filter text.
  const [saasOpen, setSaasOpen] = useState(false);
  const [saasQuery, setSaasQuery] = useState('');
  const saasBoxRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (isOpen) reset(defaults);
  }, [isOpen, reset]);

  // Close the SaaS dropdown on an outside click. Attaching the listener (and
  // the setState it triggers) happens on a real DOM event, not synchronously in
  // the effect body — so this stays clear of the React Compiler's rules.
  useEffect(() => {
    if (!saasOpen) return;
    const onPointerDown = (e: MouseEvent) => {
      if (saasBoxRef.current && !saasBoxRef.current.contains(e.target as Node)) {
        setSaasOpen(false);
      }
    };
    document.addEventListener('mousedown', onPointerDown);
    return () => document.removeEventListener('mousedown', onPointerDown);
  }, [saasOpen]);

  // useWatch rather than watch(): watch() returns a fresh function each render,
  // which the React Compiler cannot memoize safely.
  const category = useWatch({ control, name: 'category' });
  const protocol = useWatch({ control, name: 'protocol' });
  const group = useWatch({ control, name: 'connectorGroup' });
  const saasAppId = useWatch({ control, name: 'saasAppId' });
  // The derived launch URL, previewed while editing (VSNP-1938, doc 28f §4.2).
  const [wHost, wPort, wCidr, wPublic, wFqdns] = useWatch({
    control,
    name: ['targetHost', 'targetPort', 'targetCidr', 'publicHostname', 'fqdnPatternsCsv'],
  });

  const isPrivate = category === 'private';
  const isPublic = category === 'public';
  const isSaas = category === 'saas';
  const isSubnet = isPrivate && protocol === 'subnet';
  const isHostApp = isPrivate && protocol !== 'subnet';
  const creatingNewGroup = group === NEW_GROUP;
  const protoOptions = isPublic ? PUBLIC_PROTOS : PRIVATE_PROTOS;
  const selectedSaas = saasProfiles.find((s) => s.id === saasAppId);
  const previewType = toAppType(category, protocol);
  const previewPort = Number(wPort);
  const launchPreview = launchUrlFor({
    app_type: previewType,
    target_host: isHostApp ? wHost : null,
    target_port: isHostApp && Number.isInteger(previewPort) ? previewPort : null,
    target_cidr: isSubnet ? wCidr : null,
    public_hostname: isPrivate ? wPublic : null,
    fqdn_patterns: isSaas ? selectedSaas?.fqdn_patterns ?? [] : isPublic ? splitPatterns(wFqdns) : null,
  });
  const q = saasQuery.trim().toLowerCase();
  const filteredSaas = q
    ? saasProfiles.filter(
        (s) =>
          s.name.toLowerCase().includes(q) ||
          s.slug.toLowerCase().includes(q) ||
          (s.category ?? '').toLowerCase().includes(q),
      )
    : saasProfiles;

  const submit = handleSubmit(async (values) => {
    const appType = toAppType(values.category, values.protocol);

    // Resolve the connector group, creating it first if the operator chose to.
    let connectorGroup: string | undefined;
    if (values.category === 'private') {
      connectorGroup = values.connectorGroup;
      if (connectorGroup === NEW_GROUP) {
        const nn = (values.newGroupName ?? '').trim();
        try {
          await accessService.createConnectorGroup({ name: nn });
        } catch (err) {
          // 409 (already exists) is fine — reuse it; otherwise surface.
          const msg = err instanceof Error ? err.message : 'Could not create the group';
          if (!/exist/i.test(msg)) {
            setError('connectorGroup', { type: 'manual', message: msg });
            return;
          }
        }
        connectorGroup = nn;
      }
    }

    const userTags = (values.tagsCsv ?? '')
      .split(',')
      .map((t) => t.trim())
      .filter(Boolean);
    const saas = values.category === 'saas' ? saasProfiles.find((s) => s.id === values.saasAppId) : undefined;
    // A SaaS app is an `external` app whose FQDN patterns come from the catalog
    // profile; tag it so it is recognisable as catalog-sourced, and link it back
    // via saasCatalogId (that link is what blocks deleting a profile in use).
    const tags = saas ? [...new Set(['saas', saas.slug, ...userTags])] : userTags;
    const fqdnPatterns = saas
      ? saas.fqdn_patterns
      : isPublic
        ? splitPatterns(values.fqdnPatternsCsv)
        : undefined;

    await onSave({
      name: values.name,
      displayName: values.displayName || undefined,
      description: values.description || undefined,
      appType,
      targetHost: isHostApp ? values.targetHost : undefined,
      targetPort: isHostApp ? values.targetPort : undefined,
      targetCidr: isSubnet ? values.targetCidr : undefined,
      connectorGroup,
      publicHostname: isPrivate ? values.publicHostname || undefined : undefined,
      fqdnPatterns,
      saasCatalogId: saas?.id,
      tags: tags.length > 0 ? tags : undefined,
      // New apps are always created enabled; pause/resume is a table action.
      enabled: true,
    });
  });

  return (
    <Modal
      isOpen={isOpen}
      onClose={onClose}
      title="Add Application"
      footer={
        <div className="flex justify-end space-x-2">
          <button type="button" className="btn-secondary" onClick={onClose} disabled={isSubmitting}>
            Cancel
          </button>
          <button type="button" className="btn-primary" onClick={submit} disabled={isSubmitting}>
            {isSubmitting ? 'Saving…' : 'Create Application'}
          </button>
        </div>
      }
    >
      <form onSubmit={submit} className="space-y-4">
        <div className="grid grid-cols-2 gap-4">
          <AccessFormField
            label="Name"
            htmlFor="app-name"
            required
            error={errors.name?.message}
            hint="Used in policy and on the wire. Immutable after creation."
          >
            <input id="app-name" className="input-base w-full" placeholder="jira" {...register('name')} />
          </AccessFormField>

          <AccessFormField label="Display Name" htmlFor="app-display" error={errors.displayName?.message}>
            <input id="app-display" className="input-base w-full" placeholder="Jira" {...register('displayName')} />
          </AccessFormField>
        </div>

        <AccessFormField label="Description" htmlFor="app-desc" error={errors.description?.message}>
          <input id="app-desc" className="input-base w-full" {...register('description')} />
        </AccessFormField>

        {/* Type (reachability class) + Protocol (wire/targeting). For SaaS the
            single searchable picker below is the only selection control, so the
            row collapses to just Type. */}
        <div className={isSaas ? undefined : 'grid grid-cols-2 gap-4'}>
          <AccessFormField
            label="Type"
            htmlFor="app-category"
            required
            error={errors.category?.message}
            hint={isPublic ? 'A public/internet app matched by hostname (SNI) — no connector.' : undefined}
          >
            <select
              id="app-category"
              className="input-base w-full"
              {...register('category', {
                onChange: (e) => {
                  // Keep protocol valid for the new category.
                  const next = e.target.value as Category;
                  if (next === 'public' && !(PUBLIC_PROTOS as readonly string[]).includes(protocol)) {
                    setValue('protocol', 'https');
                  }
                  // Reset the SaaS picker (closed, empty filter) when
                  // leaving/entering SaaS, so it never shows a stale state.
                  setSaasOpen(false);
                  setSaasQuery('');
                  if (next !== 'saas') setValue('saasAppId', '');
                },
              })}
            >
              <option value="private">Private (reached via a connector)</option>
              <option value="public">Public (internet / FQDN)</option>
              <option value="saas">SaaS (pick from catalog)</option>
            </select>
          </AccessFormField>

          {!isSaas && (
            <AccessFormField label="Protocol" htmlFor="app-proto" required error={errors.protocol?.message}>
              <select id="app-proto" className="input-base w-full" {...register('protocol')}>
                {protoOptions.map((p) => (
                  <option key={p} value={p}>
                    {PROTO_LABEL[p]}
                  </option>
                ))}
              </select>
            </AccessFormField>
          )}
        </div>

        {/* SaaS picker — a single combobox: a dropdown-looking control that opens
            a panel with an integrated filter box + the profile list (VSNP-1150).
            Type to filter or click to select; the picked profile's domains show
            under "Included domains" below. */}
        {isSaas && (
          <AccessFormField
            label="SaaS Application"
            htmlFor="app-saas-combobox"
            required
            error={errors.saasAppId?.message}
            hint="Its domains are filled in for you."
          >
            <input type="hidden" {...register('saasAppId')} />
            <div className="relative" ref={saasBoxRef}>
              {/* The control — looks like a normal dropdown, empty by default. */}
              <button
                type="button"
                id="app-saas-combobox"
                className="input-base flex w-full items-center justify-between text-left"
                aria-haspopup="listbox"
                aria-expanded={saasOpen}
                onClick={() => setSaasOpen((o) => !o)}
              >
                <span className={selectedSaas ? 'text-gray-900' : 'text-gray-400'}>
                  {selectedSaas ? selectedSaas.name : 'Select a SaaS app…'}
                </span>
                <ChevronDown className={`h-4 w-4 text-gray-400 transition-transform ${saasOpen ? 'rotate-180' : ''}`} />
              </button>

              {saasOpen && (
                <div className="absolute z-20 mt-1 w-full rounded-md border border-gray-200 bg-white shadow-lg">
                  <div className="border-b border-gray-100 p-2">
                    <input
                      type="text"
                      autoFocus
                      className="input-base w-full"
                      placeholder="Type to filter…"
                      value={saasQuery}
                      onChange={(e) => setSaasQuery(e.target.value)}
                    />
                  </div>
                  <ul className="max-h-48 overflow-auto py-1" role="listbox">
                    {filteredSaas.length === 0 ? (
                      <li className="px-3 py-2 text-sm text-gray-400">
                        {saasProfiles.length === 0 ? 'No SaaS profiles in the catalog yet.' : 'No matches.'}
                      </li>
                    ) : (
                      filteredSaas.map((s) => {
                        const active = s.id === saasAppId;
                        return (
                          <li key={s.id} role="option" aria-selected={active}>
                            <button
                              type="button"
                              className={`flex w-full items-center justify-between px-3 py-2 text-left text-sm hover:bg-blue-50 ${
                                active ? 'bg-blue-50 font-medium text-blue-700' : 'text-gray-700'
                              }`}
                              onClick={() => {
                                setValue('saasAppId', s.id, { shouldValidate: true });
                                setValue('name', s.slug);
                                setValue('displayName', s.name);
                                setSaasQuery('');
                                setSaasOpen(false);
                              }}
                            >
                              <span>{s.name}</span>
                              {s.category && <span className="text-xs text-gray-400">{s.category}</span>}
                            </button>
                          </li>
                        );
                      })
                    )}
                  </ul>
                </div>
              )}
            </div>
          </AccessFormField>
        )}

        {/* Connector group — PRIVATE apps only */}
        {isPrivate && (
          <AccessFormField
            label="Connector Group"
            htmlFor="app-group"
            required
            error={errors.connectorGroup?.message}
            hint="Apps target a GROUP of connectors, not a single connector."
          >
            <select id="app-group" className="input-base w-full" {...register('connectorGroup')}>
              <option value="" disabled>
                Select a connector group…
              </option>
              {connectorGroups.map((g) => (
                <option key={g} value={g}>
                  {g}
                </option>
              ))}
              <option value={NEW_GROUP}>+ Create new group…</option>
            </select>
          </AccessFormField>
        )}

        {isPrivate && creatingNewGroup && (
          <AccessFormField
            label="New group name"
            htmlFor="app-newgroup"
            required
            error={errors.newGroupName?.message}
            hint="Created if it does not exist, then this app is bound to it."
          >
            <input id="app-newgroup" className="input-base w-full" placeholder="payroll-connectors" {...register('newGroupName')} />
          </AccessFormField>
        )}

        {/* Target — shape depends on Type + Protocol */}
        {isSaas ? (
          <AccessFormField
            label="Included domains"
            htmlFor="app-saas-fqdns"
            hint="Pre-filled from the catalog, matched by the hostname the client announces (TLS SNI)."
          >
            {selectedSaas ? (
              <div
                id="app-saas-fqdns"
                className="max-h-32 overflow-auto rounded border border-gray-200 bg-gray-50 p-2 font-mono text-xs text-gray-700"
              >
                {selectedSaas.fqdn_patterns.map((p) => (
                  <div key={p}>{p}</div>
                ))}
              </div>
            ) : (
              <p className="text-sm text-gray-400">Pick a SaaS application above to see its domains.</p>
            )}
          </AccessFormField>
        ) : isPublic ? (
          <AccessFormField
            label="FQDN Patterns"
            htmlFor="app-fqdns"
            required
            error={errors.fqdnPatternsCsv?.message}
            hint='A leading "*." matches all subdomains. Traffic is identified by the hostname the client announces (TLS SNI). One per line, or comma separated.'
          >
            <textarea
              id="app-fqdns"
              rows={3}
              className="input-base w-full font-mono text-sm"
              placeholder={'youtube.com\n*.googlevideo.com'}
              {...register('fqdnPatternsCsv')}
            />
          </AccessFormField>
        ) : isSubnet ? (
          <AccessFormField
            label="Target CIDR"
            htmlFor="app-cidr"
            required
            error={errors.targetCidr?.message}
            hint="Routed to the client over CONNECT-IP (full-tunnel)."
          >
            <input id="app-cidr" className="input-base w-full" placeholder="10.20.0.0/16" {...register('targetCidr')} />
          </AccessFormField>
        ) : (
          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              <AccessFormField label="Target Host" htmlFor="app-host" required error={errors.targetHost?.message}>
                <input
                  id="app-host"
                  className="input-base w-full"
                  placeholder="jira.internal.example.com"
                  {...register('targetHost')}
                />
              </AccessFormField>
            </div>
            <AccessFormField label="Target Port" htmlFor="app-port" required error={errors.targetPort?.message}>
              <input id="app-port" type="number" className="input-base w-full" {...register('targetPort')} />
            </AccessFormField>
          </div>
        )}

        <div className="grid grid-cols-2 gap-4">
          {isPrivate && (
            <AccessFormField
              label="Public Hostname"
              htmlFor="app-public"
              error={errors.publicHostname?.message}
              hint="What the Access Client resolves for this app."
            >
              <input id="app-public" className="input-base w-full" {...register('publicHostname')} />
            </AccessFormField>
          )}

          <AccessFormField
            label="Tags"
            htmlFor="app-tags"
            error={errors.tagsCsv?.message}
            hint="Comma separated. Policy rules can target tags."
          >
            <input id="app-tags" className="input-base w-full" placeholder="internal, saas" {...register('tagsCsv')} />
          </AccessFormField>
        </div>

        <AccessFormField
          label="Launch URL (derived)"
          htmlFor="app-launch-url"
          hint="What the client's Open button uses. Set a public hostname or a port to change it."
        >
          <div id="app-launch-url" className="rounded border border-gray-200 bg-gray-50 px-3 py-2 font-mono text-xs text-gray-700">
            {launchPreview ?? <span className="font-sans italic text-gray-400">None: the client offers Copy address</span>}
          </div>
        </AccessFormField>
      </form>
    </Modal>
  );
};

export default AppModal;
